import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  useTheme,
  Paper,
} from "@mui/material";
import { People, Shield, TrendingUp, AccessTime } from "@mui/icons-material";
import { LineChart, Line, XAxis, ResponsiveContainer } from "recharts";
import { palette, theme as colorTheme} from "../../../Convoze.theme";


const primary = colorTheme('palette', 'primary').value;
const pop = colorTheme('palette', 'pop').value;
const positive = colorTheme('palette', 'positive').value;
const neutral = colorTheme('palette', 'neutral').value;
const negative = colorTheme('palette', 'negative').value;



const AllUserDetails = () => {
  const theme = useTheme();

  // Sample data for the line chart
  const growthData = [
    { name: "Jan", value: 65 },
    { name: "Feb", value: 70 },
    { name: "Mar", value: 75 },
    { name: "Apr", value: 85 },
    { name: "May", value: 90 },
    { name: "Jun", value: 95 },
  ];

  // Sample data for user roles
  const roleData = [
    { name: "Company Admins", value: 86, color:primary },
    { name: "QA Analysts", value: 215, color: pop },
    { name: "Agents", value: 946, color: positive },
  ];

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Grid container spacing={3}>
        {/* Total Users Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={1}
            sx={{
              height: "100%",
              borderRadius: 2,
              border: "1px solid #e5e7eb",
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                gutterBottom
              >
                Total Users
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h4" component="div" fontWeight="bold">
                  1,247
                </Typography>
                <Box sx={{ bgcolor: "#e6f0ff", p: 1, borderRadius: 2 }}>
                  <People sx={{ color: primary }} />
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: positive,
                      mr: 1,
                    }}
                  />
                  <Typography variant="body2">Active: 1,152</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: negative,
                      mr: 1,
                    }}
                  />
                  <Typography variant="body2">Inactive: 95</Typography>
                </Box>
              </Box>
            </CardContent>
          </Paper>
        </Grid>

        {/* Users by Role Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={1}
            sx={{
              height: "100%",
              borderRadius: 2,
              border: "1px solid #e5e7eb",
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                gutterBottom
              >
                Users by Role
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="h4" component="div" fontWeight="bold">
                    3
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    role types
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: "#f5e6ff", p: 1, borderRadius: 2 }}>
                  <Shield sx={{ color: pop }} />
                </Box>
              </Box>

              {roleData.map((role, index) => (
                <Box key={index} sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2">{role.name}</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {role.value}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(role.value / 1247) * 100}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "#e0e0e0",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: role.color,
                      },
                    }}
                  />
                </Box>
              ))}
            </CardContent>
          </Paper>
        </Grid>

        {/* User Growth Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={1}
            sx={{
              height: "100%",
              borderRadius: 2,
              border: "1px solid #e5e7eb",
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                gutterBottom
              >
                User Growth
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    component="div"
                    fontWeight="bold"
                    color={positive}
                  >
                    +24%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    this month
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: "#e6f7ef", p: 1, borderRadius: 2 }}>
                  <TrendingUp sx={{ color: positive }} />
                </Box>
              </Box>

              <Box sx={{ height: 100 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={growthData}>
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#666" }}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={primary}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Paper>
        </Grid>

        {/* Active Sessions Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={1}
            sx={{
              height: "100%",
              borderRadius: 2,
              border: "1px solid #e5e7eb",
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                gutterBottom
              >
                Active Sessions
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="h4" component="div" fontWeight="bold">
                    78
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    users online
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: "#fff4e5", p: 1, borderRadius: 2 }}>
                  <AccessTime sx={{ color: neutral }} />
                </Box>
              </Box>

              <Box sx={{ mb: 1 }}>
                <img
                  src="https://images.unsplash.com/photo-1564627488683-453be0de2aad?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MjB8fHVzZXIlMjB3b3JraW5nfGVufDB8fDB8fHww"
                  alt="Users working"
                  style={{
                    width: "100%",
                    height: 80,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2">Peak today: 112 users</Typography>
                <Typography
                  variant="body2"
                  sx={{ color: positive, fontWeight: "bold" }}
                >
                  Live
                </Typography>
              </Box>
            </CardContent>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AllUserDetails;
